package com.kanati.admin.features.products

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.BannerTone
import com.kanati.admin.components.FieldRow
import com.kanati.admin.components.SectionCard
import com.kanati.admin.components.StatusBanner
import com.kanati.admin.components.TextField
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.SelectedDistrictContext
import com.kanati.admin.state.SelectedSchoolContext
import com.kanati.admin.state.formatMoney
import com.kanati.admin.state.parseMoneyInput
import com.kanati.shared.commerce.CreateProductRequest
import com.kanati.shared.commerce.ProductResponse
import com.kanati.shared.commerce.UpdateProductRequest
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

private enum class ProductFilter(val label: String) {
    Active("Active"),
    Archived("Archived"),
    All("All"),
}

private data class EditProductDialogState(
    val productId: String,
    val currentName: String,
    val currentSku: String?,
    val currentPriceAmount: Long,
)

private data class RemoveProductDialogState(
    val productId: String,
    val productName: String,
)

@Composable
fun ProductsScreen(
    apiClient: AdminApiClient,
    district: SelectedDistrictContext?,
    school: SelectedSchoolContext?,
    districtLoading: Boolean,
    districtLoadError: String?,
    theme: MaterialThemePalette,
    canEdit: Boolean,
) {
    val scope = rememberCoroutineScope()
    var products by remember { mutableStateOf<List<ProductResponse>>(emptyList()) }
    var isRefreshing by remember { mutableStateOf(false) }
    var isSubmitting by remember { mutableStateOf(false) }
    var pendingProductId by remember { mutableStateOf<String?>(null) }
    var loadError by remember { mutableStateOf<String?>(null) }
    var createError by remember { mutableStateOf<String?>(null) }
    var createSuccess by remember { mutableStateOf<String?>(null) }
    var updateError by remember { mutableStateOf<String?>(null) }
    var updateSuccess by remember { mutableStateOf<String?>(null) }
    var editDialogError by remember { mutableStateOf<String?>(null) }
    var draftName by remember { mutableStateOf("") }
    var draftSku by remember { mutableStateOf("") }
    var draftPrice by remember { mutableStateOf("") }
    var draftTaxable by remember { mutableStateOf(true) }
    var editDialog by remember { mutableStateOf<EditProductDialogState?>(null) }
    var removeDialog by remember { mutableStateOf<RemoveProductDialogState?>(null) }
    var editName by remember { mutableStateOf("") }
    var editSku by remember { mutableStateOf("") }
    var editPrice by remember { mutableStateOf("") }
    var editTaxable by remember { mutableStateOf(false) }
    var searchQuery by remember { mutableStateOf("") }
    var productFilter by remember { mutableStateOf(ProductFilter.Active) }

    val filteredProducts = products
        .filter { product ->
            when (productFilter) {
                ProductFilter.Active -> product.active
                ProductFilter.Archived -> !product.active
                ProductFilter.All -> true
            }
        }
        .let { list ->
            val q = searchQuery.trim().lowercase()
            if (q.isBlank()) list
            else list.filter { p ->
                p.name.lowercase().contains(q) ||
                    p.sku?.lowercase()?.contains(q) == true
            }
        }

    val visibleProducts = filteredProducts

    fun sortedProducts(items: List<ProductResponse>): List<ProductResponse> =
        items.sortedBy { it.name.lowercase() }

    fun mergeProduct(updatedProduct: ProductResponse) {
        var didReplace = false
        products = sortedProducts(
            products.map { existing ->
                if (existing.id == updatedProduct.id) {
                    didReplace = true
                    updatedProduct
                } else {
                    existing
                }
            }.let { items ->
                if (didReplace) items else (items + updatedProduct)
            }
        )
    }

    fun missingDistrictMessage(): String =
        when {
            districtLoading -> "Loading district context..."
            districtLoadError != null -> "District context could not be loaded. Refresh context and select a district."
            district == null -> "Select a district to view products."
            else -> "Select a school to view products."
        }

    suspend fun refreshProducts(
        selectedDistrict: SelectedDistrictContext,
        selectedSchool: SelectedSchoolContext,
    ) {
        isRefreshing = true
        loadError = null
        try {
            products = sortedProducts(apiClient.getProducts(selectedDistrict.id, selectedSchool.id))
        } catch (exception: Exception) {
            loadError = exception.message ?: "Unable to load products."
        } finally {
            isRefreshing = false
        }
    }

    fun loadProducts() {
        val selectedDistrict = district
        val selectedSchool = school
        if (selectedDistrict == null || selectedSchool == null) {
            products = emptyList()
            loadError = missingDistrictMessage()
            return
        }

        scope.launch {
            refreshProducts(selectedDistrict, selectedSchool)
        }
    }

    fun openEditDialog(product: ProductResponse) {
        editDialog = EditProductDialogState(
            productId = product.id,
            currentName = product.name,
            currentSku = product.sku,
            currentPriceAmount = product.priceAmount,
        )
        editName = product.name
        editSku = product.sku.orEmpty()
        editPrice = formatMoney(product.priceAmount).removePrefix("$")
        editTaxable = product.taxable
        editDialogError = null
        updateError = null
    }

    fun archiveProduct(dialog: RemoveProductDialogState) {
        val selectedDistrict = district
        val selectedSchool = school
        if (selectedDistrict == null || selectedSchool == null) {
            updateError = "Select a school before archiving a product."
            return
        }

        pendingProductId = dialog.productId
        updateError = null
        updateSuccess = null
        editDialogError = null

        scope.launch {
            try {
                val updatedProduct = apiClient.updateProduct(
                    productId = dialog.productId,
                    request = UpdateProductRequest(
                        districtId = selectedDistrict.id,
                        schoolId = selectedSchool.id,
                        active = false,
                    )
                )
                mergeProduct(updatedProduct)
                removeDialog = null
                updateSuccess = "\u2713 ${dialog.productName} was archived and removed from the active list."
            } catch (exception: Exception) {
                updateError = exception.message ?: "Unable to archive product."
            } finally {
                pendingProductId = null
            }
        }
    }

    fun applyProductEdits(dialog: EditProductDialogState) {
        val selectedDistrict = district
        val selectedSchool = school
        val priceAmount = parseMoneyInput(editPrice)

        when {
            selectedDistrict == null || selectedSchool == null -> editDialogError = "Select a school before updating a product."
            editName.isBlank() -> editDialogError = "Product name is required."
            priceAmount == null -> editDialogError = "Enter a valid price such as 2.75."
            else -> {
                pendingProductId = dialog.productId
                editDialogError = null
                updateError = null
                updateSuccess = null

                scope.launch {
                    try {
                        val updatedProduct = apiClient.updateProduct(
                            productId = dialog.productId,
                            request = UpdateProductRequest(
                                districtId = selectedDistrict.id,
                                schoolId = selectedSchool.id,
                                name = editName.trim(),
                                sku = editSku.trim().ifBlank { null },
                                priceAmount = priceAmount,
                                taxable = editTaxable,
                            )
                        )
                        mergeProduct(updatedProduct)
                        editDialog = null
                        updateSuccess = "\u2713 ${updatedProduct.name} was updated."
                    } catch (exception: Exception) {
                        editDialogError = exception.message ?: "Unable to update product."
                    } finally {
                        pendingProductId = null
                    }
                }
            }
        }
    }

    LaunchedEffect(district?.id, school?.id, districtLoading, districtLoadError) {
        createError = null
        createSuccess = null
        updateError = null
        updateSuccess = null
        editDialogError = null
        editDialog = null
        removeDialog = null
        pendingProductId = null
        searchQuery = ""

        val selectedDistrict = district
        val selectedSchool = school
        if (selectedDistrict != null && selectedSchool != null) {
            refreshProducts(selectedDistrict, selectedSchool)
        } else {
            products = emptyList()
            loadError = missingDistrictMessage()
        }
    }

    SectionCard(
        title = "Products",
        theme = theme,
        description = "View, create, and archive products for ${school?.name ?: "the selected school"}.",
        headerActions = {
            ActionButton(
                label = if (isRefreshing) "Loading..." else "Refresh",
                onClick = ::loadProducts,
                theme = theme,
                isPrimary = false,
                isDisabled = isRefreshing || isSubmitting || pendingProductId != null || district == null || school == null,
            )
        },
    ) {
        if (!canEdit) {
            StatusBanner(
                message = "Read-only access. Product changes require district or school edit permission.",
                theme = theme,
            )
        }
        // Search and filter row
        Div(
            attrs = {
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Row)
                    gap(12.px)
                    property("flex-wrap", "wrap")
                    property("align-items", "center")
                }
            }
        ) {
            Div(
                attrs = {
                    style {
                        property("flex", "1")
                        property("min-width", "200px")
                    }
                }
            ) {
                TextField(
                    theme = theme,
                    label = "Search",
                    value = searchQuery,
                    placeholder = "Name or SKU…",
                    onValueChange = { searchQuery = it },
                )
            }

            ProductFilterTabs(
                selected = productFilter,
                onSelect = { productFilter = it },
                theme = theme,
            )
        }

        loadError?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = if (districtLoadError != null) BannerTone.Error else BannerTone.Info,
            )
        }

        updateError?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Error,
            )
        }

        updateSuccess?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Success,
            )
        }

        val archivedProductCount = products.count { !it.active }
        if (archivedProductCount > 0 && productFilter == ProductFilter.Active) {
            StatusBanner(
                message = "$archivedProductCount archived product${if (archivedProductCount == 1) "" else "s"} hidden. Switch to Archived or All to view them.",
                theme = theme,
            )
        }

        if (visibleProducts.isEmpty() && !isRefreshing && loadError == null) {
            StatusBanner(
                message = "No active products found for ${district?.name ?: "the selected district"}.",
                theme = theme,
            )
        }

        visibleProducts.forEach { product ->
            val isPending = pendingProductId == product.id

            Div(
                attrs = {
                    style {
                        property("display", "flex")
                        property("justify-content", "space-between")
                        property("align-items", "center")
                        property("gap", "16px")
                        property("padding", "16px 18px")
                        property("border-radius", "22px")
                        property(
                            "background",
                            "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                        )
                        property("border", "1px solid ${theme.outlineVariant}")
                        property("box-shadow", "0 14px 28px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                        property("flex-wrap", "wrap")
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            property("display", "flex")
                            property("flex-direction", "column")
                            property("gap", "4px")
                            property("min-width", "220px")
                            property("flex", "1")
                        }
                    }
                ) {
                    P(
                        attrs = {
                            style {
                                property("margin", "0")
                                property("font-weight", "650")
                            }
                        }
                    ) {
                        Text(product.name)
                    }
                    P(
                        attrs = {
                            style {
                                property("margin", "0")
                                property("color", theme.onSurfaceVariant)
                                property("font-size", "0.9rem")
                            }
                        }
                    ) {
                        Text(product.sku ?: "No SKU")
                    }
                }

                Div(
                    attrs = {
                        style {
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "10px")
                            property("flex-wrap", "wrap")
                        }
                    }
                ) {
                    Div(
                        attrs = {
                            style {
                                property("padding", "8px 12px")
                                property("border-radius", "999px")
                                property("background", theme.primaryContainer)
                                property("color", theme.onPrimaryContainer)
                                property("font-weight", "700")
                                property("letter-spacing", "-0.01em")
                            }
                        }
                    ) {
                        Text(formatMoney(product.priceAmount))
                    }

                    Div(
                        attrs = {
                            style {
                                property("padding", "8px 12px")
                                property("border-radius", "999px")
                                property(
                                    "background",
                                    if (product.taxable) theme.tertiaryContainer else theme.secondaryContainer
                                )
                                property(
                                    "color",
                                    if (product.taxable) theme.onTertiaryContainer else theme.onSecondaryContainer
                                )
                                property("font-weight", "650")
                                property("font-size", "0.88rem")
                            }
                        }
                    ) {
                        Text(if (product.taxable) "Taxable" else "Tax Exempt")
                    }

                    ActionButton(
                        label = "Edit",
                        onClick = { openEditDialog(product) },
                        theme = theme,
                        isPrimary = false,
                        isDisabled = isPending,
                    )

                    ActionButton(
                        label = if (isPending) "Archiving..." else "Archive",
                        onClick = {
                            removeDialog = RemoveProductDialogState(
                                productId = product.id,
                                productName = product.name,
                            )
                            updateError = null
                            editDialogError = null
                        },
                        theme = theme,
                        isPrimary = false,
                        isDisabled = isPending,
                    )
                }
            }
        }
    }

    SectionCard(
        title = "Add Product",
        theme = theme,
        description = "Create a new product in the currently selected district.",
    ) {
        createError?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Error,
            )
        }

        createSuccess?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Success,
            )
        }

        if (district == null) {
            StatusBanner(
                message = "Select a district before adding a product.",
                theme = theme,
            )
        }

        TextField(
            theme = theme,
            label = "Name",
            value = draftName,
            placeholder = "Chocolate Milk",
            onValueChange = { draftName = it },
        )

        FieldRow {
            TextField(
                theme = theme,
                label = "SKU",
                value = draftSku,
                placeholder = "Optional",
                onValueChange = { draftSku = it },
            )
            TextField(
                theme = theme,
                label = "Price",
                value = draftPrice,
                placeholder = "2.75",
                onValueChange = { draftPrice = it },
            )
        }

        TaxableToggleField(
            theme = theme,
            isTaxable = draftTaxable,
            onToggle = { draftTaxable = !draftTaxable },
        )

        ActionButton(
            label = if (isSubmitting) "Adding..." else "Add Product",
            onClick = {
                val selectedDistrict = district
                val selectedSchool = school
                val priceAmount = parseMoneyInput(draftPrice)

                when {
                    selectedDistrict == null || selectedSchool == null -> createError = "Select a school before creating a product."
                    draftName.isBlank() -> createError = "Product name is required."
                    priceAmount == null -> createError = "Enter a valid price such as 2.75."
                    else -> scope.launch {
                        isSubmitting = true
                        createError = null
                        try {
                            val createdProduct = apiClient.createProduct(
                                CreateProductRequest(
                                    districtId = selectedDistrict.id,
                                    schoolId = selectedSchool.id,
                                    name = draftName.trim(),
                                    sku = draftSku.trim().ifBlank { null },
                                    priceAmount = priceAmount,
                                    taxable = draftTaxable,
                                    active = true,
                                )
                            )
                            products = sortedProducts((products + createdProduct).distinctBy(ProductResponse::id))
                            draftName = ""
                            draftSku = ""
                            draftPrice = ""
                            draftTaxable = true
                            refreshProducts(selectedDistrict, selectedSchool)
                        } catch (exception: Exception) {
                            createError = exception.message ?: "Unable to create product."
                        } finally {
                            isSubmitting = false
                        }
                    }
                }
            },
            theme = theme,
            isDisabled = isSubmitting || district == null || school == null || !canEdit,
        )
    }

    editDialog?.let { dialog ->
        ProductEditDialog(
            productName = editName,
            productSku = editSku,
            productPrice = editPrice,
            isTaxable = editTaxable,
            onNameChange = { editName = it },
            onSkuChange = { editSku = it },
            onPriceChange = { editPrice = it },
            onTaxableToggle = { editTaxable = !editTaxable },
            onCancel = {
                if (pendingProductId != dialog.productId) {
                    editDialog = null
                    editDialogError = null
                }
            },
            onApply = { applyProductEdits(dialog) },
            theme = theme,
            error = editDialogError,
            isSaving = pendingProductId == dialog.productId,
        )
    }

    removeDialog?.let { dialog ->
        ProductRemoveDialog(
            productName = dialog.productName,
            onCancel = {
                if (pendingProductId != dialog.productId) {
                    removeDialog = null
                }
            },
            onConfirm = { archiveProduct(dialog) },
            theme = theme,
            isRemoving = pendingProductId == dialog.productId,
        )
    }
}

@Composable
private fun ProductEditDialog(
    productName: String,
    productSku: String,
    productPrice: String,
    isTaxable: Boolean,
    onNameChange: (String) -> Unit,
    onSkuChange: (String) -> Unit,
    onPriceChange: (String) -> Unit,
    onTaxableToggle: () -> Unit,
    onCancel: () -> Unit,
    onApply: () -> Unit,
    theme: MaterialThemePalette,
    error: String?,
    isSaving: Boolean,
) {
    DialogOverlay(
        theme = theme,
        onDismiss = onCancel,
    ) {
        H3(
            attrs = {
                style {
                    margin(0.px)
                    property("font-family", "\"SF Pro Display\", \"Avenir Next\", sans-serif")
                    property("font-size", "1.4rem")
                    property("letter-spacing", "-0.03em")
                }
            }
        ) {
            Text("Edit Product")
        }

        P(
            attrs = {
                style {
                    margin(0.px)
                    property("color", theme.onSurfaceVariant)
                    property("line-height", "1.5")
                }
            }
        ) {
            Text("Update the product name, SKU, price, or taxability for the current district.")
        }

        error?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Error,
            )
        }

        TextField(
            theme = theme,
            label = "Name",
            value = productName,
            placeholder = "Chocolate Milk",
            onValueChange = onNameChange,
        )

        FieldRow {
            TextField(
                theme = theme,
                label = "SKU",
                value = productSku,
                placeholder = "Optional",
                onValueChange = onSkuChange,
            )
            TextField(
                theme = theme,
                label = "Price",
                value = productPrice,
                placeholder = "2.75",
                onValueChange = onPriceChange,
            )
        }

        TaxableToggleField(
            theme = theme,
            isTaxable = isTaxable,
            onToggle = onTaxableToggle,
        )

        DialogActions(
            theme = theme,
            onCancel = onCancel,
            cancelLabel = "Cancel",
            onConfirm = onApply,
            confirmLabel = if (isSaving) "Saving..." else "Apply",
            isConfirmDisabled = isSaving,
            isCancelDisabled = isSaving,
        )
    }
}

@Composable
private fun ProductRemoveDialog(
    productName: String,
    onCancel: () -> Unit,
    onConfirm: () -> Unit,
    theme: MaterialThemePalette,
    isRemoving: Boolean,
) {
    DialogOverlay(
        theme = theme,
        onDismiss = onCancel,
    ) {
        H3(
            attrs = {
                style {
                    margin(0.px)
                    property("font-family", "\"SF Pro Display\", \"Avenir Next\", sans-serif")
                    property("font-size", "1.4rem")
                    property("letter-spacing", "-0.03em")
                }
            }
        ) {
            Text("Archive Product")
        }

        P(
            attrs = {
                style {
                    margin(0.px)
                    property("color", theme.onSurfaceVariant)
                    property("line-height", "1.5")
                }
            }
        ) {
            Text("$productName will be archived and hidden from the main products list. This does not hard-delete it yet.")
        }

        DialogActions(
            theme = theme,
            onCancel = onCancel,
            cancelLabel = "Cancel",
            onConfirm = onConfirm,
            confirmLabel = if (isRemoving) "Archiving..." else "Archive",
            isConfirmDisabled = isRemoving,
            isCancelDisabled = isRemoving,
        )
    }
}

@Composable
private fun TaxableToggleField(
    theme: MaterialThemePalette,
    isTaxable: Boolean,
    onToggle: () -> Unit,
) {
    Div(
        attrs = {
            style {
                property("display", "flex")
                property("justify-content", "space-between")
                property("align-items", "center")
                property("gap", "14px")
                property("flex-wrap", "wrap")
                padding(16.px)
                property("border-radius", "20px")
                property(
                    "background",
                    "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surfaceVariant} 100%)"
                )
                property("border", "1px solid ${theme.outlineVariant}")
                property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
            }
        }
    ) {
        Div(
            attrs = {
                style {
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "4px")
                }
            }
        ) {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-size", "0.8rem")
                        property("font-weight", "700")
                        property("letter-spacing", "0.08em")
                        property("text-transform", "uppercase")
                        property("color", theme.onSurfaceVariant)
                    }
                }
            ) {
                Text("Tax")
            }
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("color", theme.onSurfaceVariant)
                        property("font-size", "0.92rem")
                    }
                }
            ) {
                Text(
                    if (isTaxable) {
                        "This product uses the district default sales tax rate when it is sold."
                    } else {
                        "This product is tax exempt and will not use the district default sales tax rate."
                    }
                )
            }
        }

        Div(
            attrs = {
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "12px")
                }
            }
        ) {
            P(
                attrs = {
                    style {
                        margin(0.px)
                        property("font-weight", "700")
                        property("font-size", "0.95rem")
                        property("min-width", "88px")
                        property("text-align", "right")
                        property("color", theme.onSurface)
                    }
                }
            ) {
                Text(if (isTaxable) "Taxable" else "Tax Exempt")
            }

            Button(
                attrs = {
                    attr("type", "button")
                    attr("role", "switch")
                    attr("aria-checked", isTaxable.toString())
                    attr(
                        "aria-label",
                        if (isTaxable) {
                            "Mark product as tax exempt"
                        } else {
                            "Mark product as taxable"
                        }
                    )
                    onClick { onToggle() }
                    style {
                        property("position", "relative")
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("width", "58px")
                        property("height", "34px")
                        property("padding", "0")
                        property("border", "none")
                        property("border-radius", "999px")
                        property(
                            "background",
                            if (isTaxable) {
                                "linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryContainer} 100%)"
                            } else {
                                "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.outlineVariant} 100%)"
                            }
                        )
                        property("box-shadow", "inset 0 1px 0 ${theme.scrim}, 0 10px 22px ${theme.shadow}")
                        property("cursor", "pointer")
                        property("transition", "background 180ms ease, box-shadow 180ms ease, transform 120ms ease")
                        property("-webkit-tap-highlight-color", "transparent")
                    }
                }
            ) {
                Div(
                    attrs = {
                        style {
                            property("position", "absolute")
                            property("top", "4px")
                            property("left", if (isTaxable) "28px" else "4px")
                            property("width", "26px")
                            property("height", "26px")
                            property("border-radius", "50%")
                            property("background", theme.surface)
                            property("box-shadow", "0 6px 16px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                            property("transition", "left 180ms ease")
                        }
                    }
                )
            }
        }
    }
}

@Composable
private fun DialogOverlay(
    theme: MaterialThemePalette,
    onDismiss: () -> Unit,
    content: @Composable () -> Unit,
) {
    Div(
        attrs = {
            style {
                property("position", "fixed")
                property("inset", "0")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("padding", "24px")
                property("background", "rgba(15, 23, 42, 0.24)")
                property("backdrop-filter", "blur(10px)")
                property("z-index", "20")
            }
            onClick { onDismiss() }
        }
    ) {
        Div(
            attrs = {
                onClick { it.stopPropagation() }
                style {
                    property("width", "min(100%, 520px)")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "16px")
                    padding(22.px)
                    property("border-radius", "28px")
                    property(
                        "background",
                        "linear-gradient(180deg, ${theme.surfaceContainerHighest} 0%, ${theme.surface} 100%)"
                    )
                    property("border", "1px solid ${theme.outlineVariant}")
                    property("box-shadow", "0 24px 60px ${theme.shadow}, inset 0 1px 0 ${theme.scrim}")
                }
            }
        ) {
            content()
        }
    }
}

@Composable
private fun DialogActions(
    theme: MaterialThemePalette,
    onCancel: () -> Unit,
    cancelLabel: String,
    onConfirm: () -> Unit,
    confirmLabel: String,
    isConfirmDisabled: Boolean,
    isCancelDisabled: Boolean,
) {
    Div(
        attrs = {
            style {
                property("display", "flex")
                property("justify-content", "flex-end")
                property("gap", "10px")
                property("flex-wrap", "wrap")
            }
        }
    ) {
        ActionButton(
            label = cancelLabel,
            onClick = onCancel,
            theme = theme,
            isPrimary = false,
            isDisabled = isCancelDisabled,
        )
        ActionButton(
            label = confirmLabel,
            onClick = onConfirm,
            theme = theme,
            isDisabled = isConfirmDisabled,
        )
    }
}

@Composable
private fun ProductFilterTabs(
    selected: ProductFilter,
    onSelect: (ProductFilter) -> Unit,
    theme: MaterialThemePalette,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Row)
                gap(6.px)
                property("flex-wrap", "wrap")
                property("align-items", "center")
            }
        }
    ) {
        ProductFilter.entries.forEach { filter ->
            val isSelected = filter == selected
            Button(
                attrs = {
                    onClick { onSelect(filter) }
                    style {
                        padding(8.px, 14.px)
                        borderRadius(14.px)
                        property(
                            "background",
                            if (isSelected) theme.primaryContainer
                            else "transparent"
                        )
                        property(
                            "color",
                            if (isSelected) theme.onPrimaryContainer else theme.onSurfaceVariant
                        )
                        property(
                            "border",
                            if (isSelected) "1.5px solid ${theme.primary}"
                            else "1.5px solid ${theme.outlineVariant}"
                        )
                        property("font-size", "0.84rem")
                        property("font-weight", "650")
                        property("cursor", "pointer")
                        property("transition", "background 120ms ease, color 120ms ease")
                        property("touch-action", "manipulation")
                        property("user-select", "none")
                    }
                }
            ) {
                Text(filter.label)
            }
        }
    }
}
